import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from "react";
import debounce from "lodash/debounce";
import { toast } from "../../utils/toast";
import AppContext from "../../context/AppContext";
import { fetchTaskForm, saveTask, fetchFieldsValue, createSubCategory, fetchSubcategories, updateSubCategory, getDcrcWorkflowNaming } from "../../service/Task";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import translations from "../../components/common/Translations";
import FieldAgentModal from "./FieldAgentModal";
import BulkUploadModal from "./BulkUploadModal";
import CommonButton from "../../components/common/CommonButton";
import ReactSelector from "../../components/common/CommonReactSelector";
import { LuMapPin } from "react-icons/lu"; // Import a map pin icon
import MapModal from "./MapModal";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { HiOutlineArrowLongLeft } from "react-icons/hi2";
import { HiOutlineArrowLongRight } from "react-icons/hi2";
import { FaCheck } from "react-icons/fa6";
import { Info, ListChecks, Plus, Upload, X, RefreshCw } from "lucide-react";
import { MdOutlineModeEdit } from "react-icons/md";
import { getPermissionByScreenCode } from "../../utils/permissions";

const TASK_FIELD_TRANSLATION_KEYS = {
    "Account No.": "accountNo",
    "Account No": "accountNo",
    Remarks: "remark",
    Remark: "remark",
    "CONS_NAME English": "consNameEnglish",
    "CONS_NAME Arabic": "consNameArabic",
    "Meter No": "meterNoLabel",
    "Meter No.": "meterNoLabel",
    "Area Code": "areacode",
    "Consumer Type": "consumerTypeLabel",
    phone: "phoneLabel",
    Phone: "phoneLabel",
    last_read_type: "lastReadTypeLabel",
    "Last reading": "lastReadingLabel",
    "Last reading date": "lastReadingDateLabel",
    average: "averageLabel",
    Activity: "Activity",
    Latitude: "latitude",
    Longitude: "longitude",
    Area: "area",
};

const labelForTaskField = (fieldName, langMode) => {
    const key = TASK_FIELD_TRANSLATION_KEYS[fieldName];
    if (key && translations[langMode][key] != null) return translations[langMode][key];
    return fieldName;
};

const CreateTask = () => {
  const customVarPerm = getPermissionByScreenCode("TASK_MANAGEMENT");
  // Per-option permissions: create -> single task + upload sheet, bulk upload -> bulk task
  // upload, bulk re-assign -> re-assign. Only the permitted tabs are enabled.
  const canCreateTask = Boolean(customVarPerm?.canCreate);
  const canBulkReAssign = Boolean(customVarPerm?.canBulkReAssign);
  const canBulkPriorityUpdate = Boolean(customVarPerm?.canBulkPriorityUpdate);
  // Land on the first tab the user is allowed to use.
  const defaultUploadMode = canCreateTask
    ? "single"
    : canBulkPriorityUpdate
      ? "bulk"
      : canBulkReAssign
        ? "reassign"
        : "single";
  const [accountNo, setAccountNo] = useState("");
  const [taskData, setTaskData] = useState({});
  const [assignOpen, setAssignOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(defaultUploadMode === "bulk");
  const [uploadMode, setUploadMode] = useState(defaultUploadMode);
  const [allowUpdateIfExists, setAllowUpdateIfExists] = useState(false);
  const [selectedfieldAgent, setselectedfieldAgent] = useState([]);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryValue, setEditingCategoryValue] = useState("");
  const [newSubCategoryArabic, setNewSubCategoryArabic] = useState("");
  const [editingCategoryArabicValue, setEditingCategoryArabicValue] = useState("");

  const [inputFields, setInputFields] = useState({
    mandatory: [],
    nonMandatory: [],
    optional: [],
  });
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [isCreateAssignLoading, setIsCreateAssignLoading] = useState(false);
  const [errors] = useState({});
  const context = useContext(AppContext);
  const { projectId, theme, langMode, selectedOrgId, setSelectedOption, setWorkflowId, activityId, isDcrcEnabled } = context;
  const isRtl = langMode === "ar" || langMode === "ur";
  const navigate = useNavigate();
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [currentMapField, setCurrentMapField] = useState(null);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [showAddSubCategoryInput, setShowAddSubCategoryInput] = useState(false);
  const [newSubCategory, setNewSubCategory] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [isNonMandatoryOpen, setIsNonMandatoryOpen] = useState(false);
  const subCatRef = useRef(null);
  const zoneOptions = {
    "Zone 1A": 1,
    "Zone 1B": 2,
    "Zone 2": 3,
    "Zone 3": 4,
    "Zone a": 5,
    "Zone b": 6,
    "Zone c": 7,
  };

  const [zoneCode, setZoneCode] = useState("");
  const [dcrcActivityOptions, setDcrcActivityOptions] = useState([]);
  const [selectedPriority, setSelectedPriority] = useState("High_Priority");

  const handleZoneChange = (e) => {
    const selectedZone = e.target.value;
    const code = zoneOptions[selectedZone];

    setTaskData((prevTaskData) => ({
      ...prevTaskData,
      "Zone": selectedZone,
      "Zone Code": code, // Auto-fill the Zone Code
    }));

    setZoneCode(code); // Update the local state for Zone Code
  };

  const handleGoBack = () => {
    const navigationPath =
      Cookies.get("viewmode") === "map" ? "/maptask" : "/task-manage";
    navigate(navigationPath);
    Cookies.remove("viewmode");
  };
  const ID = localStorage.getItem("activityId");
  if (ID === null || ID === undefined) {
    const actId = localStorage.getItem("selectedActivity")
    localStorage.setItem("activityId", actId);
  }

  const handleActivityChange = useCallback(async () => {
    try {
      const responseData = await fetchTaskForm(activityId);
      const responseValue = responseData.body.value;

      if (responseValue) {
        const { mandatoryFields, notMandatoryFields, optionalselectionField } = responseValue;
        setInputFields({
          mandatory: mandatoryFields || [],
          nonMandatory: notMandatoryFields || [],
          optional: optionalselectionField || [],
        });

        const accountField = (mandatoryFields || []).find(
          (field) => field.fieldName === "Account No."
        );
        if (accountField) {
          setAccountNo(taskData[accountField.fieldName] || "");
        }
      } else {
        console.error("API response data is invalid:", responseValue);
      }

      if (responseData.header.code === 607) {
        toast.error(translations[langMode].nodatavailvle);
        setInputFields({ mandatory: [], nonMandatory: [], optional: [] });
      }
    } catch (error) {
      console.error("Error in handleActivityChange:", error);
    }
  }, [activityId]);

  useEffect(() => {
    handleActivityChange();
  }, [activityId, handleActivityChange]);

  useEffect(() => {
    const fetchDcrcActivities = async () => {
      if (!isDcrcEnabled || !activityId) {
        setDcrcActivityOptions([]);
        return;
      }
      try {
        const data = await getDcrcWorkflowNaming(activityId);
        setDcrcActivityOptions(data.subActivityList || []);
      } catch (err) {
        console.error("Error fetching DCRC workflow naming:", err);
        setDcrcActivityOptions([]);
      }
    };
    fetchDcrcActivities();
  }, [activityId, isDcrcEnabled]);

  const handleInputChange = (fieldName, value) => {
    setTaskData((prevTaskData) => ({
      ...prevTaskData,
      [fieldName]: value,
    }));

    if (fieldName === "Account No.") {
      setAccountNo(value);
    }
  };

  const handleAddAccount = (fieldName, value) => {
    if (value.length > 0) {
      debouncedAccountFetch(value); // Use debounced function
    }
  }

  const debouncedAccountFetch = useMemo(() =>
    debounce((accountNo) => {
      (async () => {
        const deptId = localStorage.getItem("departmentId")?.replace(/"/g, "");
        console.log("Fetching data for account:", accountNo);
        console.log("Fetching data for Department id:", deptId);
        try {
          const responseData = await fetchFieldsValue(activityId, accountNo, selectedOrgId, projectId);
          const responseValue = responseData.body.value;

          if (responseValue) {
            const updatedTaskData = Object.keys(responseValue).reduce((acc, key) => {
              acc[key] = responseValue[key] || "";
              return acc;
            }, {});

            setTaskData((prevTaskData) => ({
              ...prevTaskData,
              ...updatedTaskData,
            }));
          }
          if (responseData.header.code === 600) {
            console.log(responseData.body.value);
          } else {
            toast.error(responseData.body.value);
          }
          if (responseData.header.code === 607) {
            toast.error(translations[langMode].nodatavailvle);
            setInputFields([]);
          }
        } catch (error) {
          console.error("Error in accountFetch:", error);
        }
      })();
    }, 300)
    , [activityId, selectedOrgId, projectId]); // ?? dependencies that affect fetch


  const validateTaskForm = () => {
    if (
      inputFields.mandatory.length === 0 &&
      inputFields.nonMandatory.length === 0 &&
      inputFields.optional.length === 0
    ) {
      toast.error(
        <span data-testid="error-message-text">
          {translations[langMode].noFieldsAvailable}
        </span>,
      );
      return false;
    }

    const allFields = [
      ...inputFields.mandatory,
      ...inputFields.nonMandatory,
      ...inputFields.optional,
    ];
    const hasAccountNoField = allFields.some((f) =>
      /^account\s*no\.?$/i.test(String(f.fieldName || "").trim()),
    );
    const accountValue = String(accountNo || taskData["Account No."] || "").trim();
    if (hasAccountNoField && !accountValue) {
      toast.error(
        <span data-testid="error-message-text">
          {translations[langMode].pleaseenteraccnumber}
        </span>,
      );
      return false;
    }
    for (const field of inputFields.mandatory) {
      const val = String(taskData[field.fieldName] || "").trim();
      if (!val) {
        toast.error(
          <span data-testid="error-message-text">
            {translations[langMode].pleasefillallmandtoryfield}
          </span>,
        );
        return false;
      }
    }
    return true;
  };

  const handleSaveTask = async () => {
    if (!validateTaskForm()) {
      return;
    }
      try {
        if (assignOpen) {
          setIsCreateAssignLoading(true)
        } else {
          setIsCreateLoading(true)
        }
        const subcategoryIds = selectedSubcategories.map(item => item.id);
        const response = await saveTask(
          activityId,
          taskData,
          accountNo,
          selectedfieldAgent,
          allowUpdateIfExists,
          subcategoryIds, // Pass the array of IDs here
          selectedPriority,
        );
        if (assignOpen) {
          setIsCreateAssignLoading(false)
        } else {
          setIsCreateLoading(false)
        }
        if (response.header.code === 600) {
          const backendMessage =
            response?.body?.value ??
            response?.body?.error ??
            response?.header?.message;
          if (backendMessage) {
            toast.success(backendMessage);
          }
          setTaskData({});
          setInputFields([]);
          setAccountNo("");
          setSelectedPriority("High_Priority");
          handleGoBack();
        } else {
          toast.error(response.body.value);
        }
      } catch (error) {
        if (assignOpen) {
          setIsCreateAssignLoading(false)
        } else {
          setIsCreateLoading(false)
        }
        console.error("Error in handleSaveTask:", error);
      }
  };

  useEffect(() => {
    const storedOption = localStorage.getItem("selectedActivity");
    if (storedOption) {
      setSelectedOption(storedOption);
      setWorkflowId(storedOption);
    }
  }, [setSelectedOption, setWorkflowId])

  const openMapModal = (fieldName) => {
    setCurrentMapField(fieldName);
    setIsMapModalOpen(true);
  };

  const handleLocationSelected = (lat, lon) => {
    if (currentMapField) {
      if (currentMapField.toLowerCase().includes('latitude')) {
        handleInputChange('Latitude', lat.toString()); // Update Latitude field
        handleInputChange('Longitude', lon.toString()); // Update Longitude field
      } else if (currentMapField.toLowerCase().includes('longitude')) {
        handleInputChange('Latitude', lat.toString()); // Update Latitude field
        handleInputChange('Longitude', lon.toString()); // Update Longitude field
      }
    }
    setIsMapModalOpen(false); // Close the modal after selection
    setCurrentMapField(null); // Clear the current map field
  };

  const closeMapModal = () => {
    setIsMapModalOpen(false);
    setCurrentMapField(null); // Clear the current map field
  };


  const loadData = useCallback(async () => { // Wrap loadData in useCallback
    try {
      const subCatData = await fetchSubcategories(activityId);
      console.log("Sub Category Data:", subCatData);
      const data = subCatData.body.value
      const uniqueSubCatData = Array.from(
        new Map(data.map(item => [item.categories, item])).values()
      );

      setSubCategoryOptions(uniqueSubCatData);
    } catch (error) {
      console.error("Error loading sub-categories in component:", error);
    }

  }, [activityId]);
  useEffect(() => {
    if (activityId) {
      loadData(); 
    }
  }, [loadData, activityId]);


  const handleAddSubCategory = async (name, arabicName) => {
    if (!name.trim()) return;

    try {
      const added = await createSubCategory(name.trim(), arabicName?.trim() || "", activityId);
      if (added.header && added.header.code === 600) {
        toast.success(added.body.value);
        setNewSubCategory("");
        setNewSubCategoryArabic("");
        setShowAddSubCategoryInput(false);
        loadData();
      } else {
        toast.error(added.body.value);
      }
    } catch (error) {
      console.error("Failed to add subcategory:", error);
    }
  };

  const toggleSubCategory = (item) => {
    if (selectedSubcategories.some(selectedItem => selectedItem.id === item.id)) {
      setSelectedSubcategories(prev => prev.filter(selectedItem => selectedItem.id !== item.id));
    } else {
      setSelectedSubcategories(prev => [...prev, item]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (subCatRef.current && !subCatRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleUpdateSubCategory = async (id, name, arabicName) => {
    if (!name.trim()) return;
    try {
      const updated = await updateSubCategory(id, name.trim(), arabicName?.trim() || "");
      if (updated.header && updated.header.code === 600) {
        toast.success(updated.body.value);
        setEditingCategoryId(null);
        setEditingCategoryValue("");
        setEditingCategoryArabicValue("");
        loadData();
      } else {
        toast.error(updated.body.value);
      }
    } catch (error) {
      console.error("Failed to update category:", error);
    }
  };

  const priorityOptions = [
    { value: "Low_Priority", label: translations[langMode].lowPriority },
    { value: "Medium_Priority", label: translations[langMode].mediumPriority },
    { value: "High_Priority", label: translations[langMode].highPriority },
  ];

  return (
    <div className="w-full bg-slate-50 min-h-full">
      <div className="flex items-center gap-3 px-5 pt-5">
        <button
          data-testid="go-back-button"
          onClick={handleGoBack}
          className="
                         rounded-lg
                         bg-blue-50
                         text-[#236DB4]
                         hover:bg-blue-100
                         focus:outline-none
                         focus:ring-2
                         focus:ring-[#236DB4]/30
                         transition
                         duration-200
                         ease-in-out
                         flex items-center justify-center
                         min-h-[36px] h-9 w-9
                         "
          aria-label={translations[langMode]?.gobacklabel ?? "Go back"}
        >
          {isRtl ? <HiOutlineArrowLongRight size={20} /> : <HiOutlineArrowLongLeft size={20} />}
        </button>
        <div>
          <h4
            className={`text-lg font-bold text-slate-800 ${theme === "dark"
              ? "dark-mode"
              : theme === "high-contrast"
                ? "high-contrast"
                : ""
              }`}
            data-testid="create-new-task-heading-text"
          >
            {translations[langMode].createnewtask}
          </h4>
          <p className="mt-0.5 text-xs text-slate-400">
            {translations[langMode]?.createTaskSubtitle ?? "Add a single task manually, upload many at once, or import a sheet."}
          </p>
        </div>
      </div>

      <div className="mx-5 mt-4 inline-flex items-center gap-1 rounded-lg bg-slate-100 p-1">
        <button
          type="button"
          onClick={() => { setUploadMode("single"); setBulkUploadOpen(false); }}
          disabled={!canCreateTask}
          className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${
            uploadMode === "single"
              ? "bg-white text-[#236DB4] shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
          aria-current={uploadMode === "single" ? "page" : undefined}
        >
          <Plus size={14} /> {translations[langMode]?.singleTaskUpload ?? "Single Task Upload"}
        </button>
        <button
          type="button"
          onClick={() => { setUploadMode("bulk"); setBulkUploadOpen(true); }}
          disabled={!(canCreateTask || canBulkPriorityUpdate)}
          data-testid="bulk-upload-button"
          className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${
            uploadMode === "bulk"
              ? "bg-white text-[#236DB4] shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <ListChecks size={14} /> {translations[langMode]?.bulkTaskUpload ?? "Bulk Task Upload"}
        </button>
        {canBulkReAssign && (
          <button
            type="button"
            onClick={() => { setUploadMode("reassign"); setBulkUploadOpen(false); }}
            data-testid="bulk-reassign-button"
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-all duration-200 ${
              uploadMode === "reassign"
                ? "bg-white text-[#236DB4] shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <RefreshCw size={14} /> {translations[langMode]?.bulkReAssign ?? "Bulk Re-Assign"}
          </button>
        )}
        <button
          type="button"
          onClick={() => navigate("/task/import-csv")}
          disabled={!canCreateTask}
          className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-slate-500 transition-all duration-200 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Upload size={14} /> {translations[langMode]?.uploadSheet ?? "Upload Sheet"}
        </button>
      </div>

      {uploadMode === "single" ? <div
        className={`px-5 pt-6 pb-4 mt-2 max-w-[1400px] w-full mx-auto ${theme === "dark"
          ? "dark-mode"
          : theme === "high-contrast"
            ? "high-contrast"
            : ""
          }`}
      >
        {!activityId ? (
          <p
            className="text-red-500 font-semibold mt-10 text-center"
            data-testid="select-msg-text"
          >
            {translations[langMode].selectMsg}
          </p>
        ) : inputFields.mandatory.length === 0 &&
          inputFields.nonMandatory.length === 0 &&
          inputFields.optional.length === 0 ? (
          <p
            className="text-red-500 font-semibold mt-10 text-center"
            data-testid="activty-does-not-have-field"
          >
            {translations[langMode].thisactivitydoesnothavefield}
          </p>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <section className="rounded-xl border border-slate-100 bg-white p-5">
            <div className="flex items-center gap-2 mb-3.5">
              <span className="w-5 h-5 rounded-full bg-[#236DB4] text-white text-[10px] font-bold flex items-center justify-center shrink-0">1</span>
              <h3 className={`${langMode === 'ar' ? 'text-sm ' : 'text-xs'} font-bold uppercase tracking-wide text-slate-500`} data-testid="what-do-you-want-existing-accounts-text">{translations[langMode].WhatDoYouWantExistingAccounts}</h3>
            </div>
            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${isRtl ? "text-right" : ""}`}>
              <label
                className={`text-left flex items-start gap-3 rounded-lg border p-3.5 transition-colors ${isRtl ? "flex-row-reverse" : ""} ${
                  allowUpdateIfExists === true
                    ? "border-[#236DB4] bg-blue-50/60 ring-1 ring-[#236DB4]"
                    : "border-slate-200 hover:border-slate-300"
                } ${customVarPerm?.canUpdate ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}
              >
                <input
                  type="radio"
                  name="Should_be_Updated_Exit_Account"
                  data-testid="update-existing-account-radio-button"
                  value="true"
                  checked={allowUpdateIfExists === true}
                  onChange={() => setAllowUpdateIfExists(true)}
                  disabled={!customVarPerm?.canUpdate}
                  className="sr-only"
                />
                <span className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${allowUpdateIfExists === true ? "border-[#236DB4]" : "border-slate-300"}`}>
                  {allowUpdateIfExists === true && <span className="w-2 h-2 rounded-full bg-[#236DB4]" />}
                </span>
                <span className={`block text-sm font-medium ${customVarPerm?.canUpdate ? "text-slate-700" : "text-slate-400"}`}>
                  {translations[langMode].updateAlreadyExistingAccountEntries}
                  <span className="block text-xs font-normal text-slate-400 mt-0.5">{translations[langMode]?.updateExistingAccountsHint ?? "Attach this task to account records already in the system"}</span>
                </span>
              </label>
              <label
                className={`text-left flex items-start gap-3 rounded-lg border p-3.5 transition-colors cursor-pointer ${isRtl ? "flex-row-reverse" : ""} ${
                  allowUpdateIfExists === false
                    ? "border-[#236DB4] bg-blue-50/60 ring-1 ring-[#236DB4]"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="Create_new_Account"
                  value="false"
                  data-testid="create-new-account-radio-button"
                  checked={allowUpdateIfExists === false}
                  onChange={() => setAllowUpdateIfExists(false)}
                  className="sr-only"
                />
                <span className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${allowUpdateIfExists === false ? "border-[#236DB4]" : "border-slate-300"}`}>
                  {allowUpdateIfExists === false && <span className="w-2 h-2 rounded-full bg-[#236DB4]" />}
                </span>
                <span className="block text-sm font-medium text-slate-700">
                  {translations[langMode].createNewAccountEntries}
                  <span className="block text-xs font-normal text-slate-400 mt-0.5">{translations[langMode]?.createNewAccountsHint ?? "Register a brand new account alongside this task"}</span>
                </span>
              </label>
            </div>
            </section>

            <section className="rounded-xl border border-slate-100 bg-white p-5">
            <h3 className={`${langMode === 'ar' ? 'text-sm ' : 'text-xs'} font-bold uppercase tracking-wide text-slate-500 flex items-center gap-2`}><span className="w-5 h-5 rounded-full bg-[#236DB4] text-white text-[10px] font-bold flex items-center justify-center shrink-0">2</span>{translations[langMode].MandatoryFields}</h3>
            <div className="grid grid-cols-1 gap-x-4 gap-y-4 lg:grid-cols-8 sm:grid-cols-4 py-2">
              {inputFields.mandatory.map((field, index) => (
                <>
                  <div className="sm:col-span-2" key={`mandatory_${index}`}>
                    <div className="relative">
                      <label
                        htmlFor={`input_${field.taskDetailsMetadataId}`}
                        className="block text-sm font-medium text-slate-700"
                        data-testid={`mandatory-field-label-${field.taskDetailsMetadataId}`}
                      >
                        {labelForTaskField(field.fieldName, langMode)}
                        <span className="text-red-500"> *</span>
                      </label>
                      {isDcrcEnabled && field.fieldName === "Activity" ? (
                        <select
                          id={`input_${field.taskDetailsMetadataId}`}
                          className="mt-1 block w-full shadow-sm sm:text-sm border-slate-200 rounded-md"
                          value={taskData[field.fieldName] || ""}
                          onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                          required
                        >
                          <option value="">{translations[langMode]?.selectActivityLabel ?? "Select Activity"}</option>
                          {dcrcActivityOptions.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <div className="relative mt-1">
                          <input
                          data-testid={`mandatory-field-input-${field.taskDetailsMetadataId}`}
                            id={`input_${field.taskDetailsMetadataId}`}
                            type={
                              field.fieldType === "LONG" ||
                                field.fieldType === "DOUBLE"
                                ? "number"
                                : "text"
                            }
                            className={`block w-full shadow-sm sm:text-sm border-slate-200 rounded-md ${
                              /latitude|longitude/i.test(field.fieldName) ? "pe-11" : ""
                            }`}
                            placeholder={`${translations[langMode]?.enter ?? "Enter"} ${labelForTaskField(field.fieldName, langMode).toLowerCase()}`}
                            value={taskData[field.fieldName] || ""}
                            onChange={(e) =>
                              handleInputChange(field.fieldName, e.target.value)
                            }
                            onKeyUp={(e) => e.key === 'Enter' && handleAddAccount(field.fieldName, e.target.value)}
                            required
                          />
                          {(field.fieldName.toLowerCase().includes('latitude') || field.fieldName.toLowerCase().includes('longitude')) && (
                            <div
                              className="absolute inset-y-0 end-0 flex w-10 items-center justify-center cursor-pointer"
                              onClick={() => openMapModal(field.fieldName)}
                              title={translations[langMode]?.selectOnMap ?? "Select on Map"}
                            >
                              <LuMapPin className="h-5 w-5 text-[#236DB4] hover:text-[#1d5a96]" aria-hidden="true" />
                            </div>
                          )}
                        </div>
                      )}
                      {errors[field.fieldName] && (
                        <p className="text-red-500 text-sm">
                          {errors[field.fieldName]}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              ))}
              <div className="sm:col-span-2">
                <label
                  htmlFor="task-priority"
                  className="block text-sm font-medium text-slate-700"
                  data-testid="task-priority-label"
                >
                  {translations[langMode].taskpriority}
                </label>
                <div className="mt-1">
                  <ReactSelector
                    dataTestId="task-priority-select"
                    value={priorityOptions.find((opt) => opt.value === selectedPriority) || null}
                    options={priorityOptions}
                    onChange={(selected) => setSelectedPriority(selected?.value || "")}
                    placeholder={translations[langMode].taskpriority}
                    height="38px"
                    borderRadius="6px"
                    fontSize="14px"
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>
            </div>
            </section>
            </div>

            <section className="rounded-xl border border-slate-100 bg-white p-5">
            <h3 className={`${langMode === 'ar' ? 'text-sm ' : 'text-xs'} font-bold uppercase tracking-wide text-slate-500 flex items-center gap-2`}><span className="w-5 h-5 rounded-full bg-slate-300 text-white text-[10px] font-bold flex items-center justify-center shrink-0">3</span>{translations[langMode].optionalSelectedfield}<span className="text-[11px] font-normal normal-case text-slate-400">— fill in only what applies</span></h3>
            <div className=" relative grid grid-cols-1 gap-x-4 gap-y-4 lg:grid-cols-8 sm:grid-cols-4 py-2">
              {inputFields.optional.map((field, index) => (
                <>
                  {isDcrcEnabled && field.fieldName === "Activity" && (
                    <div className="relative sm:col-span-2" key={`optional_activity_${index}`}>
                      <label htmlFor={`input_${field.taskDetailsMetadataId}`} className="block text-sm font-medium text-slate-700" data-testid={`optional-field-label-${field.taskDetailsMetadataId}`}>
                        {labelForTaskField(field.fieldName, langMode)}
                      </label>
                      <select
                        id={`input_${field.taskDetailsMetadataId}`}
                        className="mt-1 block w-full shadow-sm sm:text-sm border-slate-200 rounded-md"
                        value={taskData[field.fieldName] || ""}
                        onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                      >
                        <option value="">{translations[langMode]?.selectActivityLabel ?? "Select Activity"}</option>
                        {dcrcActivityOptions.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {field.fieldName === "sub_category" && (
                    <div className="relative sm:col-span-2" ref={subCatRef}>
                      <label htmlFor="subCategory" className="block text-sm font-medium text-slate-700 pb-1" data-testid={`subcategory-field-label-${field.taskDetailsMetadataId}`}>
                        {translations[langMode].subcategory}
                      </label>
                      {!showAddSubCategoryInput ? (
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowDropdown(!showDropdown)}
                            className="mt-1 w-full text-left flex items-center justify-between pl-3 pr-3 py-2 text-base border border-slate-200 focus:outline-none focus:ring-[#236DB4]/30 focus:border-[#236DB4] sm:text-sm rounded-md bg-white"
                          >
                            {selectedSubcategories.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {selectedSubcategories.map((subcategory) => (
                                  <span
                                    key={subcategory.id}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-blue-50 text-[#236DB4]"
                                  >
                                    {subcategory.categories}{subcategory.arabicName && <span className="ml-2 ">({subcategory.arabicName}) </span>}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent dropdown from closing when removing an item
                                        toggleSubCategory(subcategory); // Re-use your existing toggle function
                                      }}
                                      className="ml-1 -mr-0.5 h-4 w-4 rounded-full flex items-center justify-center hover:bg-blue-100"
                                    >
                                      <svg
                                        className="h-2 w-2"
                                        stroke="currentColor"
                                        fill="none"
                                        viewBox="0 0 8 8"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeWidth="1.5"
                                          d="M1 1l6 6m0-6L1 7"
                                        />
                                      </svg>
                                    </button>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span>{translations[langMode].selectsubcategory}</span>
                            )}
                            <ChevronDownIcon className="h-5 w-5 text-slate-400" />
                          </button>
                          {showDropdown && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                              <div
                                onClick={() => setShowAddSubCategoryInput(true)}
                                className="px-4 py-2 text-sm text-[#236DB4]  cursor-pointer"
                              >
                                {translations[langMode].addcategory}
                              </div>
                              {subCategoryOptions.map((item) => (
                                <div key={item.id} className={`flex items-center px-4 py-2 text-sm hover:bg-slate-50 ${isRtl ? "flex-row-reverse gap-3" : "gap-2"}`}>
                                  <input
                                    type="checkbox"
                                    value={item.id}
                                    checked={selectedSubcategories.some(selectedItem => selectedItem.id === item.id)}
                                    onChange={() => toggleSubCategory(item)}
                                    className=""
                                  />

                                  {editingCategoryId === item.id ? (
                                    <div className="flex flex-col gap-2 flex-1">
                                      <input
                                        type="text"
                                        value={editingCategoryValue}
                                        onChange={(e) => setEditingCategoryValue(e.target.value)}
                                        placeholder={translations[langMode]?.categoryName ?? "Category name"}
                                        className="w-full border border-slate-200 rounded-sm p-1 text-sm"
                                      />
                                      <input
                                        type="text"
                                        value={editingCategoryArabicValue}
                                        onChange={(e) => setEditingCategoryArabicValue(e.target.value)}
                                        placeholder={translations[langMode]?.arabicNameOptional ?? "Arabic name (optional)"}
                                        className="w-full border border-slate-200 rounded-sm p-1 text-sm"
                                      />
                                      <div className="flex space-x-2">
                                        <button
                                          onClick={() => handleUpdateSubCategory(item.id, editingCategoryValue, editingCategoryArabicValue)}
                                          className="inline-flex items-center px-1 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#236DB4] hover:bg-[#1d5a96]"
                                        >
                                          <FaCheck size={18} />
                                        </button>
                                        <button
                                          onClick={() => {
                                            setEditingCategoryId(null);
                                            setEditingCategoryValue("");
                                            setEditingCategoryArabicValue("");
                                          }}
                                          className="inline-flex items-center px-1 py-1  border border-slate-200 text-sm font-medium rounded-md shadow-sm text-slate-700 bg-white hover:bg-slate-50"
                                        >
                                          <X size={18} />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex justify-between items-center flex-1">
                                      <span>
                                        {item.categories}
                                        {item.arabicName && (
                                          <span className="ml-2 text-slate-700 text-sm">({item.arabicName})</span>
                                        )}
                                      </span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingCategoryId(item.id);
                                          setEditingCategoryValue(item.categories);
                                          setEditingCategoryArabicValue(item.arabicName || "");
                                        }}
                                        className="ml-2 text-[#236DB4] hover:text-[#236DB4]"
                                      >
                                        <MdOutlineModeEdit size={16} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}

                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2 mt-1 w-full">
                          <input
                            type="text"
                            value={newSubCategory}
                            onChange={(e) => setNewSubCategory(e.target.value)}
                            placeholder={translations[langMode]?.enterNewCategory ?? "Enter new category"}
                            className="flex-1 block w-full border-slate-200 rounded-md shadow-sm focus:ring-[#236DB4]/30 focus:border-[#236DB4] sm:text-sm p-2"
                          />
                          <input
                            type="text"
                            value={newSubCategoryArabic}
                            onChange={(e) => setNewSubCategoryArabic(e.target.value)}
                            placeholder={translations[langMode]?.enterArabicNameOptional ?? "Enter Arabic name (optional)"}
                            className="flex-1 block w-full border-slate-200 rounded-md shadow-sm focus:ring-[#236DB4]/30 focus:border-[#236DB4] sm:text-sm p-2"
                          />

                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleAddSubCategory(newSubCategory, newSubCategoryArabic)}
                              className="inline-flex items-center px-2 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#236DB4] hover:bg-[#1d5a96]"
                            >
                              <FaCheck size={20} />
                            </button>
                            <button
                              onClick={() => {
                                setShowAddSubCategoryInput(false);
                                setNewSubCategory("");
                                setNewSubCategoryArabic("");
                              }}
                              className="inline-flex items-center px-2 py-2 border border-slate-200 text-sm font-medium rounded-md shadow-sm text-slate-700 bg-white hover:bg-slate-50"
                            >
                              <X size={20} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ))}
            </div>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setIsNonMandatoryOpen((prev) => !prev)}
                className="w-full flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-left hover:bg-slate-100"
                aria-expanded={isNonMandatoryOpen}
              >
                <h3 className={`${langMode === 'ar' ? 'text-sm ' : 'text-sm'} font-semibold text-slate-600 flex items-center gap-2`}>
                  Show {inputFields.nonMandatory.length} more non-mandatory fields
                  <span className="hidden sm:inline-flex items-center gap-1 text-xs font-normal text-slate-400 normal-case">
                    <Info size={11} /> {translations[langMode]?.nonMandatoryFieldsHint ?? "additional details, only fill in what applies"}
                  </span>
                </h3>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span>{inputFields.nonMandatory.length}</span>
                  <ChevronDownIcon
                    className={`h-5 w-5 transition-transform duration-200 ${isNonMandatoryOpen ? "rotate-180" : ""}`}
                  />
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${isNonMandatoryOpen ? "max-h-[20000px] opacity-100" : "max-h-0 opacity-0"}`}
              >
                <div className="grid grid-cols-1 gap-x-4 gap-y-4 lg:grid-cols-8 sm:grid-cols-4 py-2">
                  {inputFields.nonMandatory.map((field, index) => (
                    <div className="sm:col-span-2" key={`nonMandatory_${index}`}>
                      <div className="relative">
                        <label
                          htmlFor={`input_${field.taskDetailsMetadataId}`}
                          className="block text-sm font-medium text-slate-700"
                          data-testid={`not-mandatory-field-label-${field.taskDetailsMetadataId}`}
                        >
                          {labelForTaskField(field.fieldName, langMode)}
                        </label>

                        {isDcrcEnabled && field.fieldName === "Activity" ? (
                          <select
                            id={`input_${field.taskDetailsMetadataId}`}
                            className="mt-1 block w-full shadow-sm sm:text-sm border-slate-200 rounded-md"
                            value={taskData[field.fieldName] || ""}
                            onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                          >
                            <option value="">{translations[langMode]?.selectActivityLabel ?? "Select Activity"}</option>
                            {dcrcActivityOptions.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : projectId === 19 && field.fieldName === "Zone" ? (
                          <select
                            id={`input_${field.taskDetailsMetadataId}`}
                            className="mt-1 block w-full shadow-sm sm:text-sm border-slate-200 rounded-md"
                            value={taskData[field.fieldName] || ""}
                            onChange={handleZoneChange}
                          >
                            <option value="">{translations[langMode]?.selectAZone ?? "Select a Zone"}</option>
                            {Object.keys(zoneOptions).map((zone) => (
                              <option key={zone} value={zone}>
                                {zone}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            data-testid={`not-mandatory-field-input-${field.taskDetailsMetadataId}`}
                            id={`input_${field.taskDetailsMetadataId}`}
                            type={field.fieldType === "DOUBLE" ? "number" : "text"}
                            className="mt-1 block w-full shadow-sm sm:text-sm border-slate-200 rounded-md"
                            placeholder={`${translations[langMode]?.enter ?? "Enter"} ${labelForTaskField(field.fieldName, langMode).toLowerCase()}`}
                            value={taskData[field.fieldName] || ""}
                            onChange={(e) =>
                              handleInputChange(field.fieldName, e.target.value)
                            }
                            readOnly={projectId === 19 && field.fieldName === "Zone Code"}
                            onKeyUp={(e) => e.key === 'Enter' && handleAddAccount(field.fieldName, e.target.value)}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            </section>

            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 bg-white px-5 py-4">
                <CommonButton colorIntent="danger" onClick={handleGoBack} variant="danger"
                  tooltipText={translations[langMode].gobacktheprivious}
                  tooltipPosition="top"
                  data-testid="create-task-cancel-button">
                  {translations[langMode].cancel}
                </CommonButton>
                <CommonButton colorIntent="success" tooltipText={translations[langMode].createamanualtask} tooltipPosition="top" onClick={handleSaveTask} variant="outline" className={`${isCreateLoading ? "bg-opacity-60" : "bg-opacity-100"}`} data-testid="save-task-button">
                  {isCreateLoading ? translations[langMode].Processing : translations[langMode].create}
                </CommonButton>
                <CommonButton colorIntent="primary"
                  onClick={() => {
                    if (validateTaskForm()) {
                      setAssignOpen(true);
                    }
                  }}
                  variant="primary"
                  className="whitespace-nowrap"
                  tooltipText={translations[langMode].createandassingtasktofield}
                  tooltipPosition="top"
                  data-testid="create-and-assign-task-button">
                  {translations[langMode].CreateandAssigne}
                </CommonButton>
            </div>
          </div>
        )}
      </div> : uploadMode === "bulk" ? (
        <BulkUploadModal
          key="bulk-upload"
          inline
          bulkUploadOpen
          setBulkUploadOpen={(open) => {
            setBulkUploadOpen(open);
            if (!open) setUploadMode("single");
          }}
          allowUpdateIfExists={allowUpdateIfExists}
        />
      ) : uploadMode === "reassign" ? (
        <BulkUploadModal
          key="bulk-reassign"
          inline
          isReassign
          bulkUploadOpen
          setBulkUploadOpen={(open) => {
            if (!open) setUploadMode("single");
          }}
          allowUpdateIfExists={allowUpdateIfExists}
        />
      ) : null}
      {assignOpen && (
        <FieldAgentModal
          assignOpen={assignOpen}
          setAssignOpen={setAssignOpen}
          handleSaveTask={handleSaveTask}
          selectedfieldAgent={selectedfieldAgent}
          setselectedfieldAgent={setselectedfieldAgent}
          isCreateLoading={isCreateAssignLoading}
        />
      )}
      {bulkUploadOpen && uploadMode !== "bulk" && uploadMode !== "reassign" && (
        <BulkUploadModal
          bulkUploadOpen={bulkUploadOpen}
          setBulkUploadOpen={setBulkUploadOpen}
          allowUpdateIfExists={allowUpdateIfExists}
        />
      )}

      <MapModal
        isOpen={isMapModalOpen}
        onClose={closeMapModal}
        onLocationSelect={handleLocationSelected}
        initialLat={parseFloat(taskData['Latitude'])} // Pass existing Lat/Lon to center the map
        initialLon={parseFloat(taskData['Longitude'])}
      />
    </div>
  );
};

export default CreateTask;

